import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lightbulb, ArrowLeft, CheckCircle2, XCircle, Sparkles, Loader2, MapPin } from "lucide-react";
import { C } from "./sopsUi";

const SOURCE_LABEL = {
  repeated_question: "Repeated question", failed_search: "Failed search",
  workflow_confusion: "Workflow confusion", admin_requested: "Admin requested", user_requested: "Requested",
};

export default function SopSuggestions() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null);
  const [genning, setGenning] = useState(false);

  const load = async (status = tab) => {
    setItems(null);
    try {
      const { data } = await api.get("/help-assistant/suggestions", { params: { status } });
      setItems(data.items || []); setCounts(data.counts || {});
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const generate = async () => {
    setGenning(true);
    try {
      const { data } = await api.post("/help-assistant/suggestions/generate");
      toast.success(data.created ? `${data.created} new suggestion(s) drafted.` : (data.message || "No new suggestions."));
      setTab("pending"); load("pending");
    } catch (e) { toast.error(errMsg(e)); } finally { setGenning(false); }
  };
  const approve = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/help-assistant/suggestions/${s.id}/approve`);
      toast.success("Approved — draft SOP created (not published).");
      navigate(`/sops/${data.sop_id}/edit`);
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const reject = async (s) => {
    const reason = window.prompt("Reject this suggestion? Optional reason:") ?? null;
    if (reason === null) return;
    setBusy(s.id);
    try { await api.post(`/help-assistant/suggestions/${s.id}/reject`, { reason }); toast.success("Rejected."); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="sop-suggestions-page">
      <button onClick={() => navigate("/sops")} className="text-[13px] flex items-center gap-1 mb-3" style={{ color: C.slate }}>
        <ArrowLeft size={14} /> Back to SOP Library
      </button>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
            <Lightbulb size={22} style={{ color: C.navy }} /> SOP Suggestions
          </h1>
          <p className="text-[13px] mt-0.5" style={{ color: C.slate }}>
            Ideas from repeated questions, failed searches and requests. Approving creates a <b>draft</b> — nothing is auto-published.
          </p>
        </div>
        <Button data-testid="sug-generate-btn" onClick={generate} disabled={genning} className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
          {genning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate from recent questions
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-transparent p-0 gap-1">
          {["pending", "approved", "rejected"].map((st) => (
            <TabsTrigger key={st} value={st} data-testid={`sug-tab-${st}`}
              className="text-[13px] px-3 py-1.5 rounded-full border data-[state=active]:bg-[#1C4358] data-[state=active]:text-white"
              style={{ borderColor: C.line, color: C.navy }}>
              {st[0].toUpperCase() + st.slice(1)}{counts[st] ? ` (${counts[st]})` : ""}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {items === null && <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>}
      {items && items.length === 0 && (
        <Card className="p-10 text-center border-[#E6EDF2]" style={{ color: C.slate }} data-testid="sug-empty">
          No {tab} suggestions. Try “Generate from recent questions”.
        </Card>
      )}
      <div className="space-y-3">
        {items?.map((s) => (
          <Card key={s.id} className="p-4 border-[#E6EDF2]" data-testid={`sug-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-semibold text-[15px]" style={{ color: C.navy }}>{s.title}</div>
                <p className="text-[12.5px] mt-0.5" style={{ color: C.slate }}>{s.reason}</p>
              </div>
              {s.created_by === "system" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "rgba(28,67,88,0.1)", color: C.navy }}><Sparkles size={9} className="inline" /> AI</span>}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {(s.source_signals || []).map((sig) => (
                <span key={sig} className="text-[10.5px] px-1.5 py-0.5 rounded-full border" style={{ borderColor: C.line, color: C.slate }}>{SOURCE_LABEL[sig] || sig}</span>
              ))}
              <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(28,67,88,0.06)", color: C.navy }}>{s.category?.replace(/_/g, " ")}</span>
              {s.related_route && <span className="text-[11px] flex items-center gap-0.5" style={{ color: C.slate }}><MapPin size={10} /> {s.related_route}</span>}
              {s.occurrences > 1 && <span className="text-[11px]" style={{ color: C.slate }}>· {s.occurrences}×</span>}
            </div>
            {(s.draft_outline || []).length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-[12.5px]" style={{ color: C.charcoal }}>
                {s.draft_outline.slice(0, 5).map((st, i) => <li key={i}>{st.title || st.detail}</li>)}
              </ul>
            )}
            {tab === "pending" && (
              <div className="flex justify-end gap-2 mt-3">
                <Button size="sm" variant="outline" disabled={busy === s.id} onClick={() => reject(s)} data-testid={`sug-reject-${s.id}`} className="h-8 gap-1 border-[#D6E0E7] text-[#9C1E22]"><XCircle size={13} /> Reject</Button>
                <Button size="sm" disabled={busy === s.id} onClick={() => approve(s)} data-testid={`sug-approve-${s.id}`} className="h-8 gap-1 bg-[#2F7D5A] hover:bg-[#276849] text-white"><CheckCircle2 size={13} /> Approve → draft</Button>
              </div>
            )}
            {s.status === "approved" && s.sop_id && (
              <div className="flex justify-end mt-3"><Button size="sm" variant="outline" onClick={() => navigate(`/sops/${s.sop_id}`)} className="h-8 border-[#D6E0E7]" style={{ color: C.navy }}>Open draft SOP</Button></div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
